import { useMutation } from "@apollo/client";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  BULK_INVITE_WITH_USER_IDS,
  VALIDATE_FOR_SELECTION_BULK_INVITE,
} from "../gql/mutations/event";
import { RECORD_INVITES } from "../gql/mutations/outreach";
import {
  COMMITMENT_LABELS,
  ENGAGEMENT_LABELS,
  ENGLISH_SKILLS_NAMES,
  FADER_LABELS,
  FILTER_DEFINITIONS,
  INTERESTS_NAMES,
  SUBSCRIPTION_TYPES,
  TENURE_LABELS,
  USER_PERMISSIONS,
  ZONE_COLOURS,
  ZONE_LABELS,
} from "../helpers/const";
import { Spacing } from "../types/enum";
import {
  type CommitmentLevel,
  type EngagementLevel,
  type EnglishSkill,
  type EventPermissions,
  type FaderLevel,
  type Interest,
  type MembershipPlans,
  SpeakingLevelCategories,
  type TenureBucket,
  type ZoneLabel,
} from "../types/graphql";

/** English-level labels (the API filters on SpeakingLevelCategories). */
const ENGLISH_LEVEL_LABELS = new Map<SpeakingLevelCategories, string>([
  [SpeakingLevelCategories.Dependent, "Dependent"],
  [SpeakingLevelCategories.IntermediateLow, "Intermediate Low"],
  [SpeakingLevelCategories.IntermediateMid, "Intermediate Mid"],
  [SpeakingLevelCategories.IntermediateHigh, "Intermediate High"],
  [SpeakingLevelCategories.AdvancedLow, "Advanced Low"],
  [SpeakingLevelCategories.AdvancedMid, "Advanced Mid"],
  [SpeakingLevelCategories.AdvancedHigh, "Advanced High"],
  [SpeakingLevelCategories.Proficient, "Proficient"],
]);

const extractEventId = (input: string): string => {
  const match = input.trim().match(/event\/([0-9a-fA-F-]{36})/);
  return match ? match[1] : input.trim();
};

/** A labelled multi-select of enum values, with an optional info tooltip. */
function MultiSelect<T extends string>({
  label,
  info,
  options,
  labels,
  value,
  onChange,
  renderDot,
}: {
  label: string;
  info?: string;
  options: T[];
  labels: Map<T, string>;
  value: T[];
  onChange: (next: T[]) => void;
  renderDot?: (option: T) => string | undefined;
}) {
  const labelNode = info ? (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
      {label}
      <Tooltip title={info} arrow>
        <InfoIcon sx={{ fontSize: 15, opacity: 0.7 }} />
      </Tooltip>
    </Box>
  ) : (
    label
  );

  return (
    <TextField
      select
      label={labelNode}
      value={value}
      SelectProps={{
        multiple: true,
        renderValue: (selected) =>
          (selected as T[]).map((option) => labels.get(option) ?? option).join(", "),
      }}
      onChange={(event) => onChange(event.target.value as unknown as T[])}
      fullWidth
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          <Checkbox checked={value.includes(option)} />
          {renderDot?.(option) && (
            <Box
              component="span"
              mr={Spacing.s}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: renderDot(option),
                display: "inline-block",
              }}
            />
          )}
          {labels.get(option) ?? option}
        </MenuItem>
      ))}
    </TextField>
  );
}

const InviteCohortPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [eventInput, setEventInput] = useState("");
  const [faderLevels, setFaderLevels] = useState<FaderLevel[]>([]);
  const [engagementLevels, setEngagementLevels] = useState<EngagementLevel[]>([]);
  const [commitmentLevels, setCommitmentLevels] = useState<CommitmentLevel[]>([]);
  const [tenures, setTenures] = useState<TenureBucket[]>([]);
  const [zones, setZones] = useState<ZoneLabel[]>([]);
  const [plan, setPlan] = useState<MembershipPlans[]>([]);
  const [englishLevels, setEnglishLevels] = useState<SpeakingLevelCategories[]>([]);
  const [englishSkills, setEnglishSkills] = useState<EnglishSkill[]>([]);
  const [interests, setInterests] = useState<Interest[]>([]);
  const [kind, setKind] = useState<EventPermissions[]>([]);
  const [validated, setValidated] = useState<{
    /** The event and filters this preview was made from — see `snapshot` below. */
    snapshot: string;
    eventId: string;
    eventTitle: string;
    userIds: string[];
    warnings: string[];
  } | null>(null);

  const eventId = extractEventId(eventInput);

  const [validate, { loading: validating }] = useMutation(VALIDATE_FOR_SELECTION_BULK_INVITE);
  const [invite, { loading: inviting }] = useMutation(BULK_INVITE_WITH_USER_IDS);
  const [recordCohort] = useMutation(RECORD_INVITES);

  const filters = {
    faderLevels: faderLevels.length ? faderLevels : undefined,
    engagementLevels: engagementLevels.length ? engagementLevels : undefined,
    commitmentLevels: commitmentLevels.length ? commitmentLevels : undefined,
    tenures: tenures.length ? tenures : undefined,
    zones: zones.length ? zones : undefined,
    plan: plan.length ? plan : undefined,
    englishLevels: englishLevels.length ? englishLevels : undefined,
    englishSkills: englishSkills.length ? englishSkills : undefined,
    interests: interests.length ? interests : undefined,
    kind: kind.length ? kind : undefined,
    all: true,
  };

  /**
   * The cohort in words, one entry per chosen option, kept with the send.
   *
   * This tool has no fixed goal — it's whatever this week needs — so the only
   * question worth asking of it is which cohorts we keep building. Nothing can
   * reconstruct that afterwards: the log records who was invited, never what
   * they were picked for. Grouped by filter so the board can count "Risk" as
   * well as "Risk: Dormant".
   */
  const filterLabels = [
    ...faderLevels.map((value) => `Risk: ${value}`),
    ...engagementLevels.map((value) => `Engagement: ${value}`),
    ...commitmentLevels.map((value) => `Commitment: ${value}`),
    ...tenures.map((value) => `Tenure: ${value}`),
    ...zones.map((value) => `Zone: ${value}`),
    ...plan.map((value) => `Plan: ${value}`),
    ...englishLevels.map((value) => `English: ${value}`),
    ...englishSkills.map((value) => `Skill: ${value}`),
    ...interests.map((value) => `Interest: ${value}`),
    ...kind.map((value) => `Event kind: ${value}`),
  ];

  /**
   * What this screen is currently asking for. A preview is only good for the
   * event and filters it was made from: change either afterwards and the ids it
   * produced describe a different cohort, or belong to a different event. Ana,
   * on #4069 — the ids were kept and could be sent against whatever was on
   * screen at the time. Now the preview carries its own snapshot and is treated
   * as out of date the moment the screen stops matching it.
   */
  const snapshot = JSON.stringify({ eventId, filters });
  const previewIsStale = validated !== null && validated.snapshot !== snapshot;

  const hasCohort =
    faderLevels.length +
      engagementLevels.length +
      commitmentLevels.length +
      tenures.length +
      zones.length +
      plan.length +
      englishLevels.length +
      englishSkills.length +
      interests.length +
      kind.length >
    0;

  const onPreview = async () => {
    if (!eventId) {
      enqueueSnackbar("Paste the event link or id first.", { variant: "info" });
      return;
    }
    if (!hasCohort) {
      enqueueSnackbar("Pick at least one cohort filter.", { variant: "info" });
      return;
    }
    try {
      const { data } = await validate({ variables: { eventId, filters } });
      const result = data?.validateForSelectionBulkInvite;
      setValidated({
        snapshot,
        eventId,
        eventTitle: result?.eventTitle ?? "",
        userIds: result?.userIds ?? [],
        warnings: result?.warningMessages ?? [],
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Preview failed.", {
        variant: "error",
      });
    }
  };

  const onInvite = async () => {
    if (!validated || validated.userIds.length === 0) {
      enqueueSnackbar("Preview the cohort first.", { variant: "info" });
      return;
    }
    if (previewIsStale) {
      enqueueSnackbar("The event or filters changed — preview the cohort again.", {
        variant: "warning",
      });
      return;
    }
    try {
      // The event the preview was made against, not whatever is in the box now.
      const { data } = await invite({
        variables: { eventId: validated.eventId, input: { userIds: validated.userIds } },
      });
      const messages = data?.bulkInviteWithUserIds?.warningMessages ?? [];
      // The count the server created, not the count we asked it to create.
      const invitedCount = data?.bulkInviteWithUserIds?.invitedUserIds?.length ?? 0;
      enqueueSnackbar(`Invited ${invitedCount} member(s) to ${validated.eventTitle}.`, {
        variant: invitedCount > 0 ? "success" : "info",
      });
      if (messages.length) {
        enqueueSnackbar(messages.join(" "), { variant: "info" });
      }
      // Record the invites to the outreach log, so the cohort shows on the
      // Outreach Impact board and counts as recent contact. Best-effort: a
      // logging failure must never make a send that already happened look failed.
      try {
        await recordCohort({
          variables: {
            eventId,
            userIds: validated.userIds,
            source: "INVITE_COHORT",
            filters: filterLabels,
          },
        });
      } catch {
        // The invite succeeded regardless — the log is a record, not a gate.
      }
      setValidated(null);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Invite failed.", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <PageHeader title="Invite a Cohort">
        Build a group of members by risk, tenure and zone, then invite them all to one event.
        Preview does a dry run — it shows who would be invited and invites no one — so you can check
        before you send.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Stack spacing={Spacing.m}>
            <TextField
              label="Event link or id"
              placeholder="https://app.heylady.io/event/… or the id"
              value={eventInput}
              onChange={(event) => setEventInput(event.target.value)}
              fullWidth
            />

            <Typography variant="body2" color="textSecondary">
              Cohort — leave a filter empty to include everyone on it.
            </Typography>

            <Box display="grid" gap={Spacing.m} gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}>
              <MultiSelect
                label="Risk (fader level)"
                info={FILTER_DEFINITIONS.fader}
                options={[...FADER_LABELS.keys()]}
                labels={FADER_LABELS}
                value={faderLevels}
                onChange={setFaderLevels}
              />
              <MultiSelect
                label="Tenure"
                info={FILTER_DEFINITIONS.tenure}
                options={[...TENURE_LABELS.keys()]}
                labels={TENURE_LABELS}
                value={tenures}
                onChange={setTenures}
              />
              <MultiSelect
                label="Engagement"
                info={FILTER_DEFINITIONS.engagement}
                options={[...ENGAGEMENT_LABELS.keys()]}
                labels={ENGAGEMENT_LABELS}
                value={engagementLevels}
                onChange={setEngagementLevels}
              />
              <MultiSelect
                label="Commitment"
                info={FILTER_DEFINITIONS.commitment}
                options={[...COMMITMENT_LABELS.keys()]}
                labels={COMMITMENT_LABELS}
                value={commitmentLevels}
                onChange={setCommitmentLevels}
              />
              <MultiSelect
                label="Colour zone"
                info={FILTER_DEFINITIONS.zone}
                options={[...ZONE_LABELS.keys()]}
                labels={ZONE_LABELS}
                value={zones}
                onChange={setZones}
                renderDot={(zone) => ZONE_COLOURS.get(zone)}
              />
              <MultiSelect
                label="Membership plan"
                options={[...SUBSCRIPTION_TYPES.keys()]}
                labels={SUBSCRIPTION_TYPES}
                value={plan}
                onChange={setPlan}
              />
              <MultiSelect
                label="English level"
                options={[...ENGLISH_LEVEL_LABELS.keys()]}
                labels={ENGLISH_LEVEL_LABELS}
                value={englishLevels}
                onChange={setEnglishLevels}
              />
              <MultiSelect
                label="English skills"
                options={[...ENGLISH_SKILLS_NAMES.keys()]}
                labels={ENGLISH_SKILLS_NAMES}
                value={englishSkills}
                onChange={setEnglishSkills}
              />
              <MultiSelect
                label="Interests"
                options={[...INTERESTS_NAMES.keys()]}
                labels={INTERESTS_NAMES}
                value={interests}
                onChange={setInterests}
              />
              <MultiSelect
                label="Role"
                options={[...USER_PERMISSIONS.keys()]}
                labels={USER_PERMISSIONS}
                value={kind}
                onChange={setKind}
              />
            </Box>

            {validated ? (
              <Alert
                severity={
                  previewIsStale ? "warning" : validated.userIds.length ? "info" : "warning"
                }
              >
                <Typography variant="body2">
                  {previewIsStale ? (
                    <>
                      The event or filters changed since this preview — press{" "}
                      <strong>Preview cohort</strong> again to see who would be invited now.
                    </>
                  ) : (
                    <>
                      <strong>{validated.userIds.length}</strong> member(s) would be invited to{" "}
                      <strong>{validated.eventTitle}</strong>.
                    </>
                  )}
                </Typography>
                {!previewIsStale && validated.warnings.length > 0 && (
                  <Box mt={Spacing.s} display="flex" flexWrap="wrap" gap={0.5}>
                    {validated.warnings.map((warning) => (
                      <Chip key={warning} label={warning} size="small" color="warning" />
                    ))}
                  </Box>
                )}
              </Alert>
            ) : null}

            <Box display="flex" gap={Spacing.s}>
              <Button variant="outlined" onClick={onPreview} disabled={validating}>
                {validating ? "Checking…" : "Preview cohort"}
              </Button>
              <Button
                variant="contained"
                onClick={onInvite}
                disabled={
                  inviting || !validated || previewIsStale || validated.userIds.length === 0
                }
              >
                {inviting
                  ? "Inviting…"
                  : validated && !previewIsStale
                    ? `Invite ${validated.userIds.length} member(s)`
                    : "Invite"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Card>
    </>
  );
};

export default InviteCohortPage;
